import type { ResolvedUserScope, RoleScope, UserScope } from '@hetivo/hetivo-lib-types';
import { ROLE_SCOPE } from '@hetivo/hetivo-lib-types';
import { deduplicate } from './utils';

/**
 * Filter scopes by level and extract their IDs.
 */
export function getScopeIdsByLevel(scopes: readonly UserScope[], level: RoleScope): string[] {
  return scopes.filter((s) => s.level === level).map((s) => s.id);
}

/**
 * Filter scopes by level.
 */
export function getScopesByLevel<T extends UserScope>(scopes: readonly T[], level: RoleScope): T[] {
  return scopes.filter((s) => s.level === level);
}

/**
 * Build a UserScope array from separate territory and site ID arrays.
 * Inverse of getScopeIdsByLevel.
 */
export function buildScopesFromIds(territoryIds: readonly string[], locationIds: readonly string[]): UserScope[] {
  const territoryScopes: UserScope[] = territoryIds.map((id) => ({ id, level: ROLE_SCOPE.territory }));
  const locationScopes: UserScope[] = locationIds.map((id) => ({ id, level: ROLE_SCOPE.location }));
  return [...territoryScopes, ...locationScopes];
}

/**
 * Extract unique parent territory IDs from site-level resolved scopes.
 */
export function getParentTerritoryIdsFromResolvedScopes(resolvedScopes: readonly ResolvedUserScope[]): string[] {
  const parentIds = resolvedScopes
    .filter(
      (s): s is ResolvedUserScope & { parentTerritoryId: string } =>
        s.level === ROLE_SCOPE.location && !!s.parentTerritoryId,
    )
    .map((s) => s.parentTerritoryId);
  return deduplicate(parentIds);
}
